/**
 * i18n — client-side translation context.
 *
 * Loads active languages + translation bundle from the backend; exposes
 * `useI18n()` returning `{ t, lang, setLang, languages, loaded }`.
 *
 * Key format: `"namespace.key"` (dot-separated). Unknown keys return the
 * key itself so they're spottable in the UI during development.
 */
import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "./api";

const I18nCtx = createContext(null);
const STORAGE_KEY = "mm_lang";
const DEFAULT_LANG = "en";

function interpolate(template, vars) {
  if (!vars || typeof template !== "string") return template;
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) =>
    vars[name] !== undefined ? String(vars[name]) : ""
  );
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem(STORAGE_KEY) || DEFAULT_LANG);
  const [languages, setLanguages] = useState([]);    // active languages
  const [bundle, setBundle] = useState({});          // {namespace: {key: value}}
  const [loaded, setLoaded] = useState(false);

  const loadLanguages = useCallback(async () => {
    try {
      const { data } = await api.get("/i18n/languages");
      setLanguages(data.items || []);
    } catch {
      setLanguages([{ code: "en", name: "English", native_name: "English", flag_emoji: "GB" }]);
    }
  }, []);

  const loadBundle = useCallback(async (targetLang) => {
    try {
      const { data } = await api.get(`/i18n/bundle/${targetLang}`);
      setBundle(data.bundle || {});
    } catch {
      setBundle({});
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { loadLanguages(); }, [loadLanguages]);
  useEffect(() => { loadBundle(lang); }, [lang, loadBundle]);

  const setLang = useCallback((code) => {
    const next = (code || DEFAULT_LANG).toLowerCase();
    localStorage.setItem(STORAGE_KEY, next);
    setLangState(next);
  }, []);

  const t = useCallback((key, fallback, vars) => {
    if (!key || typeof key !== "string") return fallback || "";
    const dot = key.indexOf(".");
    if (dot === -1) return interpolate(fallback || key, vars);
    const ns = key.slice(0, dot);
    const k = key.slice(dot + 1);
    const val = bundle?.[ns]?.[k];
    if (val === undefined || val === null) return interpolate(fallback || key, vars);
    return interpolate(val, vars);
  }, [bundle]);

  const reload = useCallback(async () => {
    await loadLanguages();
    await loadBundle(lang);
  }, [lang, loadBundle, loadLanguages]);

  const value = useMemo(() => ({ t, lang, setLang, languages, loaded, reload }),
                        [t, lang, setLang, languages, loaded, reload]);
  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nCtx);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}
